import React, { useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

// Inline SVG icons
const MicrophoneIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const PencilSquareIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const GlobeAltIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

const LockClosedIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
  </svg>
);

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ClipboardCheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

/**
 * Kort för en enskild anteckning
 *
 * @param {Object} props
 * @param {Object} props.note - Anteckningsobjekt
 * @param {function} props.onUpdate - Callback för att uppdatera innehåll
 * @param {function} props.onDelete - Callback för att ta bort
 * @param {function} props.onToggleVisibility - Callback för att ändra synlighet
 * @param {function} props.onToggleActionPoint - Callback för att ändra actionpunkt-status
 * @param {boolean} props.compact - Kompakt visning
 */
export function NoteCard({
  note,
  onUpdate,
  onDelete,
  onToggleVisibility,
  onToggleActionPoint,
  compact = false
}) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);
  const [editRoom, setEditRoom] = useState(note.room || '');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isOwner = user?._id === note.createdBy?._id || user?._id === note.createdBy;

  // Formatera tid
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
    }

    return date.toLocaleDateString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Hantera spara edit
  const handleSaveEdit = async () => {
    const contentChanged = editContent.trim() && editContent !== note.content;
    const roomChanged = editRoom.trim() !== (note.room || '');
    if (!contentChanged && !roomChanged) {
      setIsEditing(false);
      setEditContent(note.content);
      setEditRoom(note.room || '');
      return;
    }

    try {
      await onUpdate(note._id, editContent.trim(), editRoom.trim() || undefined);
      setIsEditing(false);
    } catch (err) {
      console.error('Fel vid uppdatering:', err);
    }
  };

  // Visa bekräftelse-modal för radering
  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  // Bekräfta radering
  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(note._id);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Fel vid borttagning:', err);
      setIsDeleting(false);
    }
  };

  // Hantera visibility toggle
  const handleToggleVisibility = async () => {
    try {
      const newVisibility = note.visibility === 'shared' ? 'internal' : 'shared';
      await onToggleVisibility(note._id, newVisibility);
    } catch (err) {
      console.error('Fel vid ändring av synlighet:', err);
    }
  };

  // Hantera actionpoint toggle
  const handleToggleActionPoint = async () => {
    if (!onToggleActionPoint) return;
    try {
      await onToggleActionPoint(note._id, !note.isActionPoint);
    } catch (err) {
      console.error('Fel vid ändring av actionpunkt-status:', err);
    }
  };

  return (
    <div
      className={`group border rounded-lg transition-all
        ${note.isActionPoint
          ? 'bg-pink-50 border-leadme-pink/30'
          : note.visibility === 'shared'
            ? 'bg-blue-50 border-blue-200'
            : 'bg-white border-gray-200'
        }
        ${isDeleting ? 'opacity-50' : ''}
        ${compact ? 'p-2' : 'p-3'}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {/* Skapare */}
          <span className={`font-medium text-gray-900 truncate ${compact ? 'text-xs' : 'text-sm'}`}>
            {note.createdBy?.firstName || 'Okänd'} {note.createdBy?.lastName?.[0] || ''}.
          </span>

          {/* Tid */}
          <span className={`text-gray-500 ${compact ? 'text-xs' : 'text-xs'}`}>
            {formatTime(note.createdAt)}
          </span>

          {/* Röstindikator */}
          {note.inputMethod === 'voice' && (
            <MicrophoneIcon className="w-3.5 h-3.5 text-gray-400" title="Dikterad anteckning" />
          )}

          {/* Synlighets-badge */}
          <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs
              ${note.visibility === 'shared'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600'
              }`}
            title={note.visibility === 'shared' ? 'Delad med alla' : 'Endast du'}
          >
            {note.visibility === 'shared' ? (
              <GlobeAltIcon className="w-3 h-3" />
            ) : (
              <LockClosedIcon className="w-3 h-3" />
            )}
          </span>

          {/* Actionpunkt-badge */}
          {note.isActionPoint && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-leadme-pink/20 text-leadme-pink"
              title="Actionpunkt"
            >
              <ClipboardCheckIcon className="w-3 h-3" />
            </span>
          )}
        </div>

        {/* Actions (synliga på hover eller om ägare) */}
        {isOwner && !isEditing && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleToggleVisibility}
              className="p-1 text-gray-400 hover:text-blue-600 rounded transition-colors"
              title={note.visibility === 'shared' ? 'Gör privat' : 'Dela med alla'}
            >
              {note.visibility === 'shared' ? (
                <LockClosedIcon className="w-4 h-4" />
              ) : (
                <GlobeAltIcon className="w-4 h-4" />
              )}
            </button>
            {onToggleActionPoint && (
              <button
                onClick={handleToggleActionPoint}
                className={`p-1 rounded transition-colors ${
                  note.isActionPoint
                    ? 'text-leadme-pink hover:text-leadme-pink/70'
                    : 'text-gray-400 hover:text-leadme-pink'
                }`}
                title={note.isActionPoint ? 'Ta bort actionpunkt' : 'Gör till actionpunkt'}
              >
                <ClipboardCheckIcon className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
              title="Redigera"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
              title="Ta bort"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Rum-badge (i vanligt läge) */}
      {!isEditing && note.room && (
        <span className="inline-block px-2 py-0.5 mb-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
          {note.room}
        </span>
      )}

      {/* Content */}
      {isEditing ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full px-2 py-1 text-sm border rounded resize-none focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
            rows={3}
            autoFocus
          />
          <input
            type="text"
            value={editRoom}
            onChange={(e) => setEditRoom(e.target.value)}
            placeholder="Rum/kategori (t.ex. Badrum)"
            className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-primary focus:border-primary"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditContent(note.content);
                setEditRoom(note.room || '');
              }}
              className="p-1 text-gray-500 hover:text-gray-700 rounded"
              title="Avbryt"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleSaveEdit}
              className="p-1 text-green-600 hover:text-green-700 rounded"
              title="Spara"
            >
              <CheckIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        <p className={`text-gray-700 whitespace-pre-wrap ${compact ? 'text-xs' : 'text-sm'}`}>
          {note.content}
        </p>
      )}

      {/* Konfidens (för röstanteckningar) */}
      {note.inputMethod === 'voice' && note.voiceConfidence && !compact && (
        <div className="mt-1 text-xs text-gray-400">
          Transkriberad med {Math.round(note.voiceConfidence * 100)}% säkerhet
        </div>
      )}

      {/* Bekräftelse-modal för radering */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Ta bort anteckning"
        message="Är du säker på att du vill ta bort denna anteckning? Detta kan inte ångras."
        confirmText="Ta bort"
        cancelText="Avbryt"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

export default NoteCard;
